import { RESPONSIBLE_NAME_ADD_INFO_KEY, RESPONSIBLE_PROFILE_ID_ADD_INFO_KEY } from "../../constants/eplex/cartAddInfo";
import type { ClientProfile } from "../../../models/eplex/ClientProfile";

/**
 * Подпись колонки «Ответственный» (CAB-745).
 *
 * В данных лежит только числовой profileId: `prid` у позиции заказа и
 * `profile_id` у строки журнала, когда бэк его отдаёт. Развернуть его в человека
 * можно справочником профилей клиента — им и занимается этот модуль.
 *
 * Здесь только чистые функции — модуль не тянет за собой локаль.
 */

/** Идентификаторы приходят и числом, и строкой: `prid` — строкой. */
export function parseProfileId(value: unknown): number | null {
  const id = typeof value === "string" ? Number(value.trim()) : value;
  return typeof id === "number" && Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Имя и фамилия, иначе почта. Пустая строка означает «подписи нет» — такой
 * профиль показываем идентификатором, как и вовсе неизвестный.
 */
export function profileLabel(profile: ClientProfile): string {
  return profile.name || profile.email || "";
}

/** Справочник → карта `profileId → подпись`. Профили без подписи не кладём. */
export function buildProfileLabels(profiles: readonly ClientProfile[]): Map<number, string> {
  const labels = new Map<number, string>();

  for (const profile of profiles) {
    const label = profileLabel(profile);
    if (label) labels.set(profile.id, label);
  }

  return labels;
}

/**
 * Подпись ячейки по набору идентификаторов.
 *
 * Их может быть несколько: событие ленты ссылается на несколько позиций заказа
 * ({{oids}}), и оформить их могли разные люди. Перечисляем всех, кого знаем —
 * показать произвольного из них значит соврать. Повторы схлопываем, порядок
 * сохраняем.
 *
 * Профиль, которого нет в справочнике, в подпись не попадает вовсе. Раньше на
 * его месте стоял «ID 5071», и рядом с именем соседа это читалось как одно
 * искорёженное значение: «и айди, и наименование» (замечание к CAB-745). Номер
 * профиля человеку ничего не даёт — найти по нему кого-то в кабинете нельзя.
 * А случай не исключительный: справочник отдаёт только профили своего клиента,
 * поэтому чужой или удалённый профиль в заказе — редкая, но обычная ситуация.
 */
export function responsibleLabel(
  profileIds: Iterable<number | null | undefined>,
  labels: ReadonlyMap<number, string>,
): string {
  const seen = new Set<number>();
  const parts: string[] = [];

  for (const id of profileIds) {
    if (id === null || id === undefined || seen.has(id)) continue;
    seen.add(id);

    const label = labels.get(id);
    if (label) parts.push(label);
  }

  return parts.join(", ");
}

/** Строка, у которой спрашиваем ответственного из `add_info`. */
export interface ResponsibleAddInfoRow {
  add_info?: Record<string, unknown> | null;
}

/** Профиль и подпись, сохранённые в позиции при добавлении в корзину. */
export interface StoredResponsible {
  profileId: number | null;
  name: string;
}

/**
 * Ключи ответственного для `add_info` новой позиции корзины.
 *
 * Пустой объект означает «класть нечего»: без профиля подпись не с чем связать,
 * а строка без идентификатора только мешала бы разбирать данные потом.
 */
export function responsibleAddInfo(profileId: number | null, profileName: string): Record<string, unknown> {
  const id = parseProfileId(profileId);
  if (id === null) return {};

  const addInfo: Record<string, unknown> = { [RESPONSIBLE_PROFILE_ID_ADD_INFO_KEY]: id };

  const name = profileName.trim();
  if (name) addInfo[RESPONSIBLE_NAME_ADD_INFO_KEY] = name;

  return addInfo;
}

/** Ответственный, сохранённый в позиции при добавлении в корзину. */
export function storedResponsible(row: ResponsibleAddInfoRow): StoredResponsible {
  const addInfo = row.add_info ?? {};
  const name = addInfo[RESPONSIBLE_NAME_ADD_INFO_KEY];

  return {
    profileId: parseProfileId(addInfo[RESPONSIBLE_PROFILE_ID_ADD_INFO_KEY]),
    name: typeof name === "string" ? name.trim() : "",
  };
}

/**
 * Подпись по сохранённому в позиции ответственному.
 *
 * Справочник важнее сохранённой подписи: имя профиля могли поменять уже после
 * того, как позицию положили в корзину. Сохранённая подпись включается там, где
 * справочник профиля не знает, — иначе ячейка осталась бы пустой при том, что
 * данные о человеке у нас есть.
 */
export function storedResponsibleLabel(row: ResponsibleAddInfoRow, labels: ReadonlyMap<number, string>): string {
  const { profileId, name } = storedResponsible(row);
  if (profileId === null) return "";

  return labels.get(profileId) ?? name;
}
